import { useEffect, useState } from "react";
import { DropResult } from "@hello-pangea/dnd";
import { deleteHttp, post, put } from "@common/api/apiShared";
import { DotLegalReactQueryOptions, useDotLegalMutation, useDotLegalQueryClient, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";
import { useExpandedId } from "@common/hooks/useExpandedId";
import { useOptimisticUpdate } from "@common/hooks/useOptimisticUpdate";
import { useTranslation } from "@localization/useTranslation";
import { PolicyViewModel } from "../policyOverview/PolicyOverview.types";
import { usePolicyVersionSectionsQuery } from "../policyCompareVersions/PolicyCompareVersions.hooks";
import {
    PolicyControl,
    PolicyImportSaveModel,
    PolicySection,
    PolicySectionControlLinksModel,
    PolicySectionReorderItem,
    PolicySectionSaveModel,
    PolicySectionsResponse,
    PolicySectionUpdateModel,
    PolicySubsection,
    PolicySubsectionReorderItem,
    PolicySubsectionSaveModel,
    PolicySubsectionUpdateModel,
    PolicyVersionDetailsUpdateModel,
} from "./PolicySections.types";

// Bundle of section/subsection mutations passed down to the rows.
export interface PolicySectionActions {
    updateSectionTitle: (sectionId: string, title: string) => void;
    updateSectionContent: (sectionId: string, content: string) => void;
    setSectionControls: (sectionId: string, frameworkControlIds: string[]) => void;
    requestDeleteSection: (section: PolicySection) => void;
    addSubsection: (sectionId: string, targetIndex: number) => void;
    addSectionAfter: (sectionId: string) => void;
    addSectionAfterSubsection: (sectionId: string, subsectionId: string) => void;
    updateSubsectionTitle: (sectionId: string, subsectionId: string, title: string) => void;
    updateSubsectionContent: (sectionId: string, subsectionId: string, content: string) => void;
    setSubsectionControls: (sectionId: string, subsectionId: string, frameworkControlIds: string[]) => void;
    setSubsectionForDeletion: (value: { sectionId: string; subsection: PolicySubsection } | undefined) => void;
    // Edit-on-demand: only one content editor is live at a time. startEditingContent makes the given
    // section/subsection (or the description sentinel) the live editor; everything else stays static HTML.
    startEditingContent: (contentId: string) => void;
    stopEditing: () => void;
}

// Sentinel content id for the policy description, so it shares the single-live-editor scheme with sections.
export const policyDescriptionEditId = "policy-description";

export function sectionsQueryKey(policyId: string) {
    return `policy-sections-${policyId}`;
}

function mapSubsectionsForSection(
    current: PolicySectionsResponse,
    sectionId: string,
    transform: (subsections: PolicySubsection[]) => PolicySubsection[]
): PolicySectionsResponse {
    return {
        ...current,
        sections: current.sections.map((section) => (section.id === sectionId ? { ...section, subsections: transform(section.subsections) } : section)),
    };
}

export function usePolicySectionsQuery(policyId: string, options?: DotLegalReactQueryOptions) {
    return useDotLegalQueryWithKey<PolicySectionsResponse>(sectionsQueryKey(policyId), `/policies/${policyId}/sections`, options);
}

function policyOverviewQueryKey(policyId: string) {
    return `/policies/${policyId}`;
}

// Starting, importing and discarding a draft all change the version set. The sections come back in the
// response (set directly); the overview is refetched because it also carries the version list the editor
// reads (top-bar data + compare button) and that shape isn't derivable from this response.
//
// The overview also drives the action-bar buttons (publish/discard/import/start) via hasDraft. Patch that
// flag synchronously — in the same render the sections cache flips isEditable — so the buttons don't briefly
// show a stale set before the async refetch lands. hasDraft mirrors the draft's editability across all three
// ops: start/import create an editable draft; discard reverts to a non-editable published version.
function syncDraftCaches(queryClient: ReturnType<typeof useDotLegalQueryClient>, policyId: string, sections: PolicySectionsResponse) {
    queryClient.setQueryData(sectionsQueryKey(policyId), sections);
    const overview = queryClient.getQueryData<PolicyViewModel>(policyOverviewQueryKey(policyId));
    if (overview) queryClient.setQueryData<PolicyViewModel>(policyOverviewQueryKey(policyId), { ...overview, hasDraft: sections.isEditable });
    queryClient.refetchQueries(policyOverviewQueryKey(policyId));
}

export function useStartPolicyDraft(policyId: string) {
    const queryClient = useDotLegalQueryClient();
    return useDotLegalMutation(() => post<PolicySectionsResponse>(`/policies/${policyId}/draft`, undefined), {
        onSuccess: (response) => {
            if (response.success()) syncDraftCaches(queryClient, policyId, response.value());
        },
    });
}

export function useImportPolicyDraft(policyId: string) {
    const queryClient = useDotLegalQueryClient();
    return useDotLegalMutation((model: PolicyImportSaveModel) => post<PolicySectionsResponse>(`/policies/${policyId}/import`, model), {
        onSuccess: (response) => {
            if (response.success()) syncDraftCaches(queryClient, policyId, response.value());
        },
    });
}

export function useDiscardPolicyDraft(policyId: string) {
    const queryClient = useDotLegalQueryClient();
    return useDotLegalMutation(() => deleteHttp<PolicySectionsResponse>(`/policies/${policyId}/draft`), {
        onSuccess: (response) => {
            if (response.success()) syncDraftCaches(queryClient, policyId, response.value());
        },
    });
}

export function useCreatePolicySection(policyId: string) {
    const { setQueryData } = useOptimisticUpdate();
    return useDotLegalMutation((model: PolicySectionSaveModel) => post<PolicySectionsResponse>(`/policies/${policyId}/sections`, model), {
        onSuccess: (response) => {
            if (response.success()) {
                setQueryData(sectionsQueryKey(policyId), response.value());
            }
        },
    });
}

export function useUpdatePolicySection(policyId: string) {
    const queryClient = useDotLegalQueryClient();
    const { putOnQueueAndSetQueryData } = useOptimisticUpdate();
    const mutation = useDotLegalMutation((vars: { sectionId: string; model: PolicySectionUpdateModel }) =>
        put<PolicySectionsResponse>(`/policies/${policyId}/sections/${vars.sectionId}`, vars.model)
    );

    function update(sectionId: string, change: Partial<Pick<PolicySection, "title" | "content">>) {
        const current = queryClient.getQueryData<PolicySectionsResponse>(sectionsQueryKey(policyId));
        if (!current) return;

        const updatedSections = current.sections.map((section) => (section.id === sectionId ? { ...section, ...change } : section));
        const updatedSection = updatedSections.find((section) => section.id === sectionId);
        if (!updatedSection) return;

        const optimistic: PolicySectionsResponse = { ...current, sections: updatedSections };
        const model: PolicySectionUpdateModel = { title: updatedSection.title, content: updatedSection.content };
        putOnQueueAndSetQueryData(optimistic, sectionsQueryKey(policyId), mutation.mutateAsync, { sectionId, model });
    }

    return {
        updateTitle: (sectionId: string, title: string) => update(sectionId, { title }),
        updateContent: (sectionId: string, content: string) => update(sectionId, { content }),
    };
}

export function useUpdatePolicyVersionDetails(policyId: string) {
    const queryClient = useDotLegalQueryClient();
    const { putOnQueueAndSetQueryData } = useOptimisticUpdate();
    const mutation = useDotLegalMutation((model: PolicyVersionDetailsUpdateModel) => put<PolicySectionsResponse>(`/policies/${policyId}/version`, model));

    function update(change: Partial<PolicyVersionDetailsUpdateModel>) {
        const current = queryClient.getQueryData<PolicySectionsResponse>(sectionsQueryKey(policyId));
        if (!current) return;

        const optimistic: PolicySectionsResponse = { ...current, ...change };
        const model: PolicyVersionDetailsUpdateModel = { title: optimistic.title, description: optimistic.description };
        putOnQueueAndSetQueryData(optimistic, sectionsQueryKey(policyId), mutation.mutateAsync, model);
    }

    return {
        updateTitle: (title: string) => update({ title }),
        updateDescription: (description: string) => update({ description }),
    };
}

export function useDeletePolicySection(policyId: string) {
    const queryClient = useDotLegalQueryClient();
    const { putOnQueueAndSetQueryData } = useOptimisticUpdate();
    const mutation = useDotLegalMutation((vars: { sectionId: string; deleteSubsections: boolean }) =>
        deleteHttp<PolicySectionsResponse>(`/policies/${policyId}/sections/${vars.sectionId}?deleteSubsections=${vars.deleteSubsections}`)
    );

    return (sectionId: string, deleteSubsections: boolean) => {
        const current = queryClient.getQueryData<PolicySectionsResponse>(sectionsQueryKey(policyId));
        if (!current) return;

        const sortedSections = [...current.sections].sort((a, b) => a.order - b.order);
        const targetIndex = sortedSections.findIndex((section) => section.id === sectionId);
        if (targetIndex < 0) return;

        const target = sortedSections[targetIndex];
        const previous = sortedSections[targetIndex - 1];

        const optimisticSections = current.sections
            .filter((section) => section.id !== sectionId)
            .map((section) =>
                !deleteSubsections && previous && section.id === previous.id
                    ? {
                          ...section,
                          subsections: [
                              ...section.subsections,
                              ...target.subsections
                                  .slice()
                                  .sort((a, b) => a.order - b.order)
                                  .map((subsection, index) => ({ ...subsection, order: section.subsections.length + index })),
                          ],
                      }
                    : section
            );

        const optimistic: PolicySectionsResponse = { ...current, sections: optimisticSections };
        putOnQueueAndSetQueryData(optimistic, sectionsQueryKey(policyId), mutation.mutateAsync, { sectionId, deleteSubsections });
    };
}

export function useReorderPolicySections(policyId: string) {
    const queryClient = useDotLegalQueryClient();
    const { putOnQueueAndSetQueryData } = useOptimisticUpdate();
    const mutation = useDotLegalMutation((items: PolicySectionReorderItem[]) => put<PolicySectionsResponse>(`/policies/${policyId}/sections/order`, items));

    return (reordered: PolicySection[]) => {
        const current = queryClient.getQueryData<PolicySectionsResponse>(sectionsQueryKey(policyId));
        if (!current) return;

        const items = reordered.map((section, index) => ({ sectionId: section.id, order: index }));
        const optimistic: PolicySectionsResponse = { ...current, sections: reordered.map((section, index) => ({ ...section, order: index })) };
        putOnQueueAndSetQueryData(optimistic, sectionsQueryKey(policyId), mutation.mutateAsync, items);
    };
}

export function useSetPolicySectionControls(policyId: string) {
    const queryClient = useDotLegalQueryClient();
    const { putOnQueueAndSetQueryData } = useOptimisticUpdate();
    const mutation = useDotLegalMutation((vars: { sectionId: string; model: PolicySectionControlLinksModel }) =>
        put<PolicySectionsResponse>(`/policies/${policyId}/sections/${vars.sectionId}/controls`, vars.model)
    );

    return (sectionId: string, frameworkControlIds: string[]) => {
        const current = queryClient.getQueryData<PolicySectionsResponse>(sectionsQueryKey(policyId));
        if (!current) return;

        const optimistic: PolicySectionsResponse = {
            ...current,
            sections: current.sections.map((section) => (section.id === sectionId ? { ...section, linkedControlIds: frameworkControlIds } : section)),
        };
        putOnQueueAndSetQueryData(optimistic, sectionsQueryKey(policyId), mutation.mutateAsync, { sectionId, model: { frameworkControlIds } });
    };
}

export function useAddPolicySectionAfter(policyId: string) {
    const { setQueryData } = useOptimisticUpdate();
    return useDotLegalMutation(
        (vars: { sectionId: string; model: PolicySectionSaveModel }) =>
            post<PolicySectionsResponse>(`/policies/${policyId}/sections/${vars.sectionId}/add-section-after`, vars.model),
        {
            onSuccess: (response) => {
                if (response.success()) {
                    setQueryData(sectionsQueryKey(policyId), response.value());
                }
            },
        }
    );
}

export function useAddPolicySectionAfterSubsection(policyId: string) {
    const { setQueryData } = useOptimisticUpdate();
    return useDotLegalMutation(
        (vars: { sectionId: string; subsectionId: string; model: PolicySectionSaveModel }) =>
            post<PolicySectionsResponse>(`/policies/${policyId}/sections/${vars.sectionId}/subsections/${vars.subsectionId}/add-section-after`, vars.model),
        {
            onSuccess: (response) => {
                if (response.success()) {
                    setQueryData(sectionsQueryKey(policyId), response.value());
                }
            },
        }
    );
}

export function useCreatePolicySubsection(policyId: string) {
    const { setQueryData } = useOptimisticUpdate();
    return useDotLegalMutation(
        (vars: { sectionId: string; model: PolicySubsectionSaveModel }) =>
            post<PolicySectionsResponse>(`/policies/${policyId}/sections/${vars.sectionId}/subsections`, vars.model),
        {
            onSuccess: (response) => {
                if (response.success()) {
                    setQueryData(sectionsQueryKey(policyId), response.value());
                }
            },
        }
    );
}

export function useUpdatePolicySubsection(policyId: string) {
    const queryClient = useDotLegalQueryClient();
    const { putOnQueueAndSetQueryData } = useOptimisticUpdate();
    const mutation = useDotLegalMutation((vars: { sectionId: string; subsectionId: string; model: PolicySubsectionUpdateModel }) =>
        put<PolicySectionsResponse>(`/policies/${policyId}/sections/${vars.sectionId}/subsections/${vars.subsectionId}`, vars.model)
    );

    function update(sectionId: string, subsectionId: string, change: Partial<Pick<PolicySubsection, "title" | "content">>) {
        const current = queryClient.getQueryData<PolicySectionsResponse>(sectionsQueryKey(policyId));
        if (!current) return;

        const optimistic = mapSubsectionsForSection(current, sectionId, (subsections) =>
            subsections.map((subsection) => (subsection.id === subsectionId ? { ...subsection, ...change } : subsection))
        );

        const updatedSubsection = optimistic.sections
            .find((section) => section.id === sectionId)
            ?.subsections.find((subsection) => subsection.id === subsectionId);
        if (!updatedSubsection) return;

        const model: PolicySubsectionUpdateModel = { title: updatedSubsection.title, content: updatedSubsection.content };
        putOnQueueAndSetQueryData(optimistic, sectionsQueryKey(policyId), mutation.mutateAsync, { sectionId, subsectionId, model });
    }

    return {
        updateTitle: (sectionId: string, subsectionId: string, title: string) => update(sectionId, subsectionId, { title }),
        updateContent: (sectionId: string, subsectionId: string, content: string) => update(sectionId, subsectionId, { content }),
    };
}

export function useDeletePolicySubsection(policyId: string) {
    const queryClient = useDotLegalQueryClient();
    const { putOnQueueAndSetQueryData } = useOptimisticUpdate();
    const mutation = useDotLegalMutation((vars: { sectionId: string; subsectionId: string }) =>
        deleteHttp<PolicySectionsResponse>(`/policies/${policyId}/sections/${vars.sectionId}/subsections/${vars.subsectionId}`)
    );

    return (sectionId: string, subsectionId: string) => {
        const current = queryClient.getQueryData<PolicySectionsResponse>(sectionsQueryKey(policyId));
        if (!current) return;

        const optimistic = mapSubsectionsForSection(current, sectionId, (subsections) => subsections.filter((subsection) => subsection.id !== subsectionId));
        putOnQueueAndSetQueryData(optimistic, sectionsQueryKey(policyId), mutation.mutateAsync, { sectionId, subsectionId });
    };
}

export function useReorderPolicySubsections(policyId: string) {
    const queryClient = useDotLegalQueryClient();
    const { putOnQueueAndSetQueryData } = useOptimisticUpdate();
    const mutation = useDotLegalMutation((vars: { sectionId: string; items: PolicySubsectionReorderItem[] }) =>
        put<PolicySectionsResponse>(`/policies/${policyId}/sections/${vars.sectionId}/subsections/order`, vars.items)
    );

    return (sectionId: string, reordered: PolicySubsection[]) => {
        const current = queryClient.getQueryData<PolicySectionsResponse>(sectionsQueryKey(policyId));
        if (!current) return;

        const items = reordered.map((subsection, index) => ({ subsectionId: subsection.id, order: index }));
        const optimistic = mapSubsectionsForSection(current, sectionId, () => reordered.map((subsection, index) => ({ ...subsection, order: index })));
        putOnQueueAndSetQueryData(optimistic, sectionsQueryKey(policyId), mutation.mutateAsync, { sectionId, items });
    };
}

export function useSetPolicySubsectionControls(policyId: string) {
    const queryClient = useDotLegalQueryClient();
    const { putOnQueueAndSetQueryData } = useOptimisticUpdate();
    const mutation = useDotLegalMutation((vars: { sectionId: string; subsectionId: string; model: PolicySectionControlLinksModel }) =>
        put<PolicySectionsResponse>(`/policies/${policyId}/sections/${vars.sectionId}/subsections/${vars.subsectionId}/controls`, vars.model)
    );

    return (sectionId: string, subsectionId: string, frameworkControlIds: string[]) => {
        const current = queryClient.getQueryData<PolicySectionsResponse>(sectionsQueryKey(policyId));
        if (!current) return;

        const optimistic = mapSubsectionsForSection(current, sectionId, (subsections) =>
            subsections.map((subsection) => (subsection.id === subsectionId ? { ...subsection, linkedControlIds: frameworkControlIds } : subsection))
        );
        putOnQueueAndSetQueryData(optimistic, sectionsQueryKey(policyId), mutation.mutateAsync, { sectionId, subsectionId, model: { frameworkControlIds } });
    };
}

export function useSelectableFrameworkControlsForPolicies() {
    return useDotLegalQueryWithKey<Array<PolicyControl>>("selectableFrameworkControlsForPolicies", "/frameworkControls/selectable");
}

export function usePolicySectionsEditor(policyId: string, versionId?: string) {
    const { translateString } = useTranslation();

    const currentSectionsQuery = usePolicySectionsQuery(policyId, { enabled: !versionId });
    const versionSectionsQuery = usePolicyVersionSectionsQuery(policyId, versionId);
    const sectionsQuery = versionId ? versionSectionsQuery : currentSectionsQuery;
    const availableControlsQuery = useSelectableFrameworkControlsForPolicies();

    const createSection = useCreatePolicySection(policyId);
    const addSectionAfterSubsectionMutation = useAddPolicySectionAfterSubsection(policyId);
    const addSectionAfterMutation = useAddPolicySectionAfter(policyId);
    const { updateTitle: updateSectionTitle, updateContent: updateSectionContent } = useUpdatePolicySection(policyId);
    const { updateTitle: updateVersionTitle, updateDescription: updateVersionDescription } = useUpdatePolicyVersionDetails(policyId);
    const deleteSection = useDeletePolicySection(policyId);
    const reorderSections = useReorderPolicySections(policyId);
    const setSectionControls = useSetPolicySectionControls(policyId);

    const createSubsection = useCreatePolicySubsection(policyId);
    const { updateTitle: updateSubsectionTitle, updateContent: updateSubsectionContent } = useUpdatePolicySubsection(policyId);
    const deleteSubsection = useDeletePolicySubsection(policyId);
    const reorderSubsections = useReorderPolicySubsections(policyId);
    const setSubsectionControls = useSetPolicySubsectionControls(policyId);

    const [createdSectionId, setCreatedSectionId] = useState<string | undefined>();
    const [createdSubsectionId, setCreatedSubsectionId] = useState<string | undefined>();
    const [sectionForDeletion, setSectionForDeletionState] = useState<{ section: PolicySection; deleteSubsections: boolean } | undefined>();
    const [subsectionForDeletion, setSubsectionForDeletion] = useState<{ sectionId: string; subsection: PolicySubsection } | undefined>();
    const [isReordering, setIsReordering] = useState(false);
    const [editingContentId, setEditingContentId] = useState<string | undefined>();

    const requestDeleteSection = (section: PolicySection) => setSectionForDeletionState({ section, deleteSubsections: false });
    const cancelDeleteSection = () => setSectionForDeletionState(undefined);
    const setDeleteSubsectionsChecked = (deleteSubsections: boolean) =>
        setSectionForDeletionState((current) => (current ? { ...current, deleteSubsections } : current));

    const { getExpandedId, setExpandedId } = useExpandedId();
    const focusedId = getExpandedId();

    const sections = sectionsQuery.data?.sections ?? [];
    const availableControls = availableControlsQuery.data ?? [];

    useEffect(() => {
        if (!focusedId) return;
        document.getElementById(focusedId)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, [focusedId, sectionsQuery.data]);

    const addSection = async () => {
        const response = await createSection.mutateAsync({ title: translateString("newSection"), content: "", order: sections.length });
        if (!response.success()) return;

        const updated = response.value();
        const newSection = updated.sections[updated.sections.length - 1];
        setCreatedSectionId(newSection?.id);
        if (newSection) setExpandedId(newSection.id);
    };

    const addSubsectionToLastSection = async () => {
        if (sections.length === 0) return;
        const lastSection = [...sections].sort((a, b) => a.order - b.order)[sections.length - 1];
        await addSubsection(lastSection.id, lastSection.subsections.length);
    };

    const addSectionAfter = async (sectionId: string) => {
        const previousSectionIds = new Set(sections.map((section) => section.id));
        const response = await addSectionAfterMutation.mutateAsync({
            sectionId,
            model: { title: translateString("newSection"), content: "", order: 0 },
        });
        if (!response.success()) return;

        const updated = response.value();
        const newSection = updated.sections.find((section) => !previousSectionIds.has(section.id));
        if (!newSection) return;

        setCreatedSectionId(newSection.id);
        setExpandedId(newSection.id);
    };

    const addSectionAfterSubsection = async (sectionId: string, subsectionId: string) => {
        const previousSectionIds = new Set(sections.map((section) => section.id));
        const response = await addSectionAfterSubsectionMutation.mutateAsync({
            sectionId,
            subsectionId,
            model: { title: translateString("newSection"), content: "", order: 0 },
        });
        if (!response.success()) return;

        const updated = response.value();
        const newSection = updated.sections.find((section) => !previousSectionIds.has(section.id));
        if (!newSection) return;

        setCreatedSectionId(newSection.id);
        setExpandedId(newSection.id);
    };

    const addSubsection = async (sectionId: string, targetIndex: number) => {
        const parent = sections.find((section) => section.id === sectionId);
        const previousSubsectionIds = new Set((parent?.subsections ?? []).map((subsection) => subsection.id));
        const response = await createSubsection.mutateAsync({
            sectionId,
            model: { title: translateString("newSubsection"), content: "", order: parent?.subsections.length ?? 0 },
        });
        if (!response.success()) return;

        const updatedParent = response.value().sections.find((section) => section.id === sectionId);
        const newSubsection = updatedParent?.subsections.find((subsection) => !previousSubsectionIds.has(subsection.id));
        if (!updatedParent || !newSubsection) return;

        const others = updatedParent.subsections.filter((subsection) => subsection.id !== newSubsection.id).sort((a, b) => a.order - b.order);
        const reordered = [...others.slice(0, targetIndex), newSubsection, ...others.slice(targetIndex)];
        reorderSubsections(sectionId, reordered);
        setCreatedSubsectionId(newSubsection.id);
        setExpandedId(newSubsection.id);
    };

    const handleDragEnd = (result: DropResult) => {
        const { source, destination, type } = result;
        if (!destination || source.index === destination.index) return;

        if (type === "SUBSECTION") {
            if (source.droppableId !== destination.droppableId) return;

            const sectionId = source.droppableId.replace("policy-subsections-", "");
            const parent = sections.find((section) => section.id === sectionId);
            if (!parent) return;

            const reordered = [...parent.subsections].sort((a, b) => a.order - b.order);
            const [moved] = reordered.splice(source.index, 1);
            reordered.splice(destination.index, 0, moved);

            reorderSubsections(sectionId, reordered);
            return;
        }

        const reordered = [...sections];
        const [moved] = reordered.splice(source.index, 1);
        reordered.splice(destination.index, 0, moved);

        reorderSections(reordered);
    };

    const confirmDeleteSection = () => {
        if (!sectionForDeletion) return;
        const removedIds = [sectionForDeletion.section.id, ...sectionForDeletion.section.subsections.map((subsection) => subsection.id)];
        if (editingContentId && removedIds.includes(editingContentId)) setEditingContentId(undefined);
        deleteSection(sectionForDeletion.section.id, sectionForDeletion.deleteSubsections);
        setSectionForDeletionState(undefined);
    };

    const confirmDeleteSubsection = () => {
        if (!subsectionForDeletion) return;
        if (editingContentId === subsectionForDeletion.subsection.id) setEditingContentId(undefined);
        deleteSubsection(subsectionForDeletion.sectionId, subsectionForDeletion.subsection.id);
        setSubsectionForDeletion(undefined);
    };

    const isEditable = versionId ? false : (sectionsQuery.data?.isEditable ?? false);
    const hasPublishedVersion = sectionsQuery.data?.hasPublishedVersion ?? false;
    const publishedVersionLabel = sectionsQuery.data?.publishedVersionLabel ?? null;

    const actions: PolicySectionActions = {
        updateSectionTitle,
        updateSectionContent,
        setSectionControls,
        requestDeleteSection,
        addSubsection,
        addSectionAfter,
        addSectionAfterSubsection,
        updateSubsectionTitle,
        updateSubsectionContent,
        setSubsectionControls,
        setSubsectionForDeletion,
        startEditingContent: setEditingContentId,
        stopEditing: () => setEditingContentId(undefined),
    };

    return {
        actions,
        editingContentId,
        isLoading: sectionsQuery.isLoading,
        sections,
        availableControls,
        isEditable,
        // A version preview renders read-only: titles/content as static text, collapsing empty sections.
        // Scoped to versionId so the editable current-policy view (which never passes one) is unaffected.
        isVersionPreview: !!versionId,
        isDraftOfPublishedVersion: isEditable && hasPublishedVersion,
        publishedVersionLabel,
        versionDetails: {
            title: sectionsQuery.data?.title ?? null,
            description: sectionsQuery.data?.description ?? null,
            // For a version preview the badge must show the previewed version, not the policy's current one
            // (the backend reports CurrentVersion as the latest regardless of which version is mapped).
            currentVersion: versionId && sectionsQuery.data ? `v${sectionsQuery.data.major}.${sectionsQuery.data.minor}` : sectionsQuery.data?.currentVersion,
            isEditable,
            isInitialDraft: isEditable && !hasPublishedVersion,
        },
        focusedId,
        setExpandedId,
        createdSectionId,
        createdSubsectionId,
        isAddingSection: createSection.isLoading,
        isAddingSubsection: createSubsection.isLoading,
        isReordering,
        setIsReordering,
        sectionForDeletion,
        requestDeleteSection,
        cancelDeleteSection,
        setDeleteSubsectionsChecked,
        subsectionForDeletion,
        setSubsectionForDeletion,
        addSection,
        addSectionAfter,
        isAddingSectionAfter: addSectionAfterMutation.isLoading,
        addSectionAfterSubsection,
        isAddingSectionAfterSubsection: addSectionAfterSubsectionMutation.isLoading,
        addSubsection,
        addSubsectionToLastSection,
        handleDragEnd,
        confirmDeleteSection,
        confirmDeleteSubsection,
        updateSectionTitle,
        updateSectionContent,
        setSectionControls,
        updateSubsectionTitle,
        updateSubsectionContent,
        setSubsectionControls,
        updateVersionTitle,
        updateVersionDescription,
    };
}
